import { useCallback } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate } from "@tanstack/react-router"
import {
	IconArrowsLeftRightTrailOutline18 as ArrowsLeftRight,
	IconCircleCheckOutline18 as CircleCheck,
	IconEnvelopeBanOutline18 as EnvelopeBan,
	IconPenWritingOutline18 as PencilLine,
	IconTrashOutline18 as Trash,
} from "nucleo-ui-outline-18"
import { useToggle } from "react-use"

import { generateId } from "@productlane/lib/src/browser"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import { AlertDialog, IconButton } from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuSeparator,
} from "@productlane/ui/src/components/internal/dropdown"
import { EllipsisIcon as MoreHorizontal } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { SlackIcon } from "@productlane/ui/src/icons/project/slack-icon"
import { mutators } from "@productlane/zero/mutators"
import { isBulkResolvableComment } from "@productlane/zero/mutators/comments"
import { queries } from "@productlane/zero/queries"

import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { useShortcut } from "@/lib"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useBackNavigation } from "@/lib/back-navigation"
import { isEmailBlocked } from "@/lib/email-utils"
import { getPortalBaseUrl } from "@/lib/portal-base-url"
import { useCmdkState } from "@/lib/states/cmdk-state"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { useEditorState } from "../../hooks/useEditorState"
import { useThreadSlackMessageLink } from "../../hooks/useSlackMessageLink"
import { useThreadsMultiSelectState } from "../../multi-select/threads-multi-select-state"
import { isThreadNoteEditable, useThreadContext } from "../../thread-context"
import { ThreadCopySubmenu, useThreadCopyActions } from "./top-bar-copy-menu"

interface Props {
	onHighlightButtonClick: () => unknown
	isEditorOpen: boolean
	moveOnThen: (dispatch: () => void) => void
}

export const MoreMenu = ({
	onHighlightButtonClick,
	isEditorOpen,
	moveOnThen,
}: Props) => {
	const { thread, goNext, goPrev, nextThread, prevThread } = useThreadContext()
	const { attachmentsHandlers, commentAttachmentsHandlers } = useEditorState({})
	const { role } = useRole()
	const openMergePage = useCmdkState((s) => s.openPage)

	const zero = useZero()
	const workspace = useZeroWorkspace()

	const portalUrl = getPortalBaseUrl(workspace)

	const isSupportPortalEnabled =
		workspace?.customerPortalSettings?.isEnabled === true

	const copyRequestsLink = useCallback(
		async (id: string, trackMeta?: { shortcut?: boolean }) => {
			if (!workspace || !isSupportPortalEnabled) {
				return
			}
			const url = `${portalUrl}requests?r=${id}`
			const issue = thread.issues.find((i) => i.id === id)
			const project = thread.projects.find((p) => p.id === id)
			const requestsPrivacyNote = (
				<span className="mt-2 block">
					Only this customer and their team members can open this link.
				</span>
			)
			try {
				await navigator.clipboard.writeText(url)
				if (issue) {
					toast.success({
						title: "Support portal link copied",
						time: 5500,
						descriptionAction: {
							label: `${issue.identifier}: ${issue.title}`,
							callback: () => window.open(issue.url, "_blank"),
						},
						description: requestsPrivacyNote,
					})
				} else if (project) {
					const projectUrl = project.url
					if (projectUrl) {
						toast.success({
							title: "Support portal link copied",
							time: 5500,
							descriptionAction: {
								label: project.name,
								callback: () => window.open(projectUrl, "_blank"),
							},
							description: requestsPrivacyNote,
						})
					} else {
						toast.success({
							title: "Support portal link copied",
							time: 5500,
							description: (
								<>
									<span className="font-medium text-primary">
										{project.name}
									</span>
									{requestsPrivacyNote}
								</>
							),
						})
					}
				} else if (id === thread.id) {
					const title = thread.title?.trim() || "This conversation"
					toast.success({
						title: "Support portal link copied",
						time: 5500,
						descriptionAction: {
							label: title,
							callback: () =>
								window.open(
									`${window.location.origin}/threads/${thread.id}`,
									"_blank",
								),
						},
						description: requestsPrivacyNote,
					})
				} else {
					toast.success({
						title: "Support portal link copied",
						time: 5500,
						description: requestsPrivacyNote,
					})
				}
				analytics.track("copy-feedback-public-url-link", {
					surface: "requests",
					...trackMeta,
				})
			} catch {
				toast.error({ title: "Failed to copy link to clipboard" })
			}
		},
		[workspace, isSupportPortalEnabled, portalUrl, thread],
	)

	const unresolvedCommentCount = (thread.comments ?? []).filter(
		isBulkResolvableComment,
	).length

	const resolveAllComments = useCallback(async () => {
		const result = await zero.mutate(
			mutators.comments.resolveAllInThread({ feedbackId: thread.id }),
		).client
		if (result.type === "success") {
			toast.success({
				title:
					unresolvedCommentCount === 1
						? "Comment resolved"
						: `${unresolvedCommentCount} comments resolved`,
				icon: CircleCheck,
			})
			analytics.track("resolve-all-comments", {
				count: unresolvedCommentCount,
			})
		} else {
			toast.error({
				title: "Couldn't resolve the comments on this thread.",
				icon: CircleCheck,
			})
		}
	}, [zero, thread.id, unresolvedCommentCount])

	const navigate = useNavigate()
	const [blockedSenders] = useQuery(queries.blockedSenders())
	const senderEmail = thread.contact?.email

	const markSpam = useCallback(() => {
		if (!senderEmail) return
		const threadId = thread.id
		const blockedSenderId = generateId()
		const wasBlocked = isEmailBlocked(senderEmail, blockedSenders)
		const prevState = thread.state
		const prevSnoozedUntilIso = thread.snoozedUntil
			? new Date(thread.snoozedUntil).toISOString()
			: null
		const prevLastStateChangeAtIso = thread.lastStateChangeAt
			? new Date(thread.lastStateChangeAt).toISOString()
			: null
		moveOnThen(() => {
			void zero
				.mutate(
					mutators.threads.markSpam({
						id: threadId,
						blockedSenderId,
						lastStateChangeAtIso: new Date().toISOString(),
					}),
				)
				.client.then((result) => {
					if (result.type === "success") return
					toast.error({
						title: "Couldn't mark this thread as spam",
						description:
							result.error instanceof Error ? result.error.message : undefined,
						icon: EnvelopeBan,
					})
				})
			toast.success({
				icon: EnvelopeBan,
				title: "Marked as spam",
				description: `Future emails from ${senderEmail} skip your inbox.`,
				onUndo: async () => {
					if (!wasBlocked) {
						await mutateOk(
							zero.mutate(
								mutators.blockedSenders.delete({ id: blockedSenderId }),
							),
						)
					}
					await mutateOk(
						zero.mutate(
							mutators.threads.update({
								id: threadId,
								state: prevState,
								snoozedUntilIso: prevSnoozedUntilIso,
								lastStateChangeAtIso: prevLastStateChangeAtIso,
							}),
						),
					)
					void navigate({ to: "/threads/$id", params: { id: threadId } })
				},
			})
			analytics.track("mark-thread-spam", { alreadyBlocked: wasBlocked })
		})
	}, [
		zero,
		moveOnThen,
		navigate,
		senderEmail,
		blockedSenders,
		thread.id,
		thread.state,
		thread.snoozedUntil,
		thread.lastStateChangeAt,
	])

	const { threadIds } = useThreadsMultiSelectState()
	useShortcut("meta+shift+b, ctrl+shift+b", markSpam, {
		preventDefault: true,
		enabled: role !== "VIEWER" && !!senderEmail && threadIds.length === 0,
	})

	const slackMessageLink = useThreadSlackMessageLink()

	const openInSlack = useCallback(() => {
		if (slackMessageLink) {
			window.open(slackMessageLink, "_blank")
		}
	}, [slackMessageLink])

	useShortcut("alt+s", openInSlack, {
		preventDefault: true,
		enableOnContentEditable: false,
		enabled: !!slackMessageLink,
	})

	useShortcut(
		"meta+backspace, ctrl+backspace",
		() => {
			analytics.track("open-delete-insight-dialog", { shortcut: true })
			setConfirmDelete(true)
		},
		{ enabled: role !== "VIEWER" },
	)
	const { goBack } = useBackNavigation()
	const [isConfirmDeleteOpen, setConfirmDelete] = useToggle(false)
	const ctx = api.useUtils()

	const showRequestsCopy = isSupportPortalEnabled
	const copyActions = useThreadCopyActions()

	const requestsShortcutEnabled = role !== "VIEWER" && showRequestsCopy

	useShortcut(
		"shift+s",
		() => {
			void copyRequestsLink(thread.id, { shortcut: true })
		},
		{ enabled: requestsShortcutEnabled },
	)

	return (
		<>
			<RoleCheck not={["VIEWER"]}>
				<DropdownMenu
					trigger={
						<IconButton
							Icon={MoreHorizontal}
							iconStrokeWidth={2}
							variant="ghost"
							iconClassName="fill-current"
						/>
					}
					contentProps={{ side: "bottom", align: "end" }}
				>
					<DropdownMenuGroup>
						<ThreadCopySubmenu
							actions={copyActions}
							onCopySupportPortalLink={
								showRequestsCopy
									? () => void copyRequestsLink(thread.id)
									: undefined
							}
						/>
						{slackMessageLink && (
							<DropdownMenuItem
								Icon={SlackIcon}
								shortcut={`${ShortcutSymbols.ALT}${ShortcutSymbols.S}`}
								onClick={openInSlack}
							>
								Open in Slack
							</DropdownMenuItem>
						)}
						{isThreadNoteEditable(thread) && (
							<DropdownMenuItem
								Icon={PencilLine}
								shortcut={`${ShortcutSymbols.SHIFT}${ShortcutSymbols.H}`}
								onClick={() => {
									onHighlightButtonClick()
								}}
							>
								{isEditorOpen ? "Close" : "Open"} note editor
							</DropdownMenuItem>
						)}
					</DropdownMenuGroup>
					<DropdownMenuSeparator />
					<DropdownMenuGroup>
						{unresolvedCommentCount > 0 && (
							<DropdownMenuItem
								Icon={CircleCheck}
								onClick={() => {
									void resolveAllComments()
								}}
							>
								Resolve all comments
							</DropdownMenuItem>
						)}

						<DropdownMenuItem
							Icon={ArrowsLeftRight}
							onClick={() => {
								if (!thread.contactId) {
									toast.info({
										title: "Merge needs a contact on this thread",
										description:
											"You can only merge into another thread from the same person.",
										icon: ArrowsLeftRight,
									})
									return
								}
								openMergePage("Merge into")
								analytics.track("open-merge-thread", { moreMenu: true })
							}}
						>
							Merge thread
						</DropdownMenuItem>

						{senderEmail && (
							<DropdownMenuItem
								Icon={EnvelopeBan}
								shortcut={`${ShortcutSymbols.CMD}${ShortcutSymbols.SHIFT}${ShortcutSymbols.B}`}
								onClick={markSpam}
							>
								Mark as spam
							</DropdownMenuItem>
						)}

						<DropdownMenuItem
							Icon={Trash}
							shortcut={`${ShortcutSymbols.CMD}${ShortcutSymbols.BACKSPACE}`}
							onClick={() => {
								setConfirmDelete(true)
								analytics.track("open-delete-insight-dialog")
							}}
						>
							Delete
						</DropdownMenuItem>
					</DropdownMenuGroup>
				</DropdownMenu>
			</RoleCheck>

			<AlertDialog
				isOpen={isConfirmDeleteOpen}
				onOpenChange={setConfirmDelete}
				confirmText="Delete"
				title="Are you sure?"
				description="Do you really want to delete this thread?"
				onCancel={() => {
					setConfirmDelete(false)
					analytics.track("cancel-delete-insight")
				}}
				onConfirm={async () => {
					if (nextThread) {
						goNext()
					} else if (prevThread) {
						goPrev()
					} else {
						goBack("/threads", true)
					}
					await mutateOk(
						zero.mutate(
							mutators.threads.delete({
								id: thread.id,
							}),
						),
					)
					attachmentsHandlers.clearAttachments()
					commentAttachmentsHandlers.clearAttachments()
					analytics.track("delete-thread")
					await ctx.workspace.searchResults.invalidate()
				}}
			/>
		</>
	)
}
